import { useState, useEffect } from 'react';
import { MessageCircle, RefreshCw, Search, Phone, Clock } from 'lucide-react';
import { api } from '../../utils/api';

function fmtTime(ts) {
  if (!ts) return '';
  const d = new Date(ts * 1000);
  const now = new Date();
  const diff = now - d;
  if (diff < 60000) return 'just now';
  if (diff < 3600000) return `${Math.floor(diff/60000)}m ago`;
  if (diff < 86400000) return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  return d.toLocaleDateString();
}

const TYPE_ICON = { text: '💬', image: '🖼️', document: '📄', audio: '🔊', video: '🎥', location: '📍' };

export default function WhatsAppInbox() {
  const [messages, setMessages] = useState([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const data = await api.get('/api/whatsapp/inbox');
      setMessages(Array.isArray(data) ? data : []);
    } catch { setMessages([]); }
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const filtered = messages.filter(m =>
    m.body?.toLowerCase().includes(search.toLowerCase()) ||
    m.from_number?.includes(search) ||
    m.contact_name?.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className="p-6 max-w-4xl mx-auto">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl flex items-center justify-center text-white" style={{ background: '#25D366' }}>
            <MessageCircle size={20} />
          </div>
          <div>
            <h1 className="text-xl font-bold text-gray-800">WhatsApp Inbox</h1>
            <p className="text-sm text-gray-500">{messages.filter(m => !m.is_read).length} unread messages</p>
          </div>
        </div>
        <button onClick={load} className="flex items-center gap-2 px-3 py-2 rounded-lg border text-sm hover:bg-gray-50">
          <RefreshCw size={14} className={loading ? 'animate-spin' : ''} /> Refresh
        </button>
      </div>

      {/* Search */}
      <div className="relative mb-4">
        <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
        <input value={search} onChange={e => setSearch(e.target.value)}
          placeholder="Search by name, number or message…"
          className="w-full pl-9 pr-4 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-green-300" />
      </div>

      {loading ? (
        <div className="text-center py-16 text-gray-400">Loading…</div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-16">
          <MessageCircle size={40} className="mx-auto text-gray-300 mb-3" />
          <p className="text-gray-400">{search ? 'No messages match your search' : 'No incoming messages yet'}</p>
          <p className="text-xs text-gray-400 mt-1">Messages will appear here once your Meta webhook is configured and verified</p>
        </div>
      ) : (
        <div className="space-y-2">
          {filtered.map(msg => (
            <div key={msg.id}
              className={`flex items-start gap-3 p-4 rounded-xl border transition-all ${!msg.is_read ? 'bg-green-50 border-green-200' : 'bg-white border-gray-100'}`}>
              <div className="w-10 h-10 rounded-full flex items-center justify-center text-white font-bold text-sm flex-shrink-0"
                style={{ background: !msg.is_read ? '#25D366' : '#9ca3af' }}>
                {(msg.contact_name || msg.from_number || '?')[0].toUpperCase()}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between gap-2">
                  <span className="font-semibold text-sm text-gray-800">{msg.contact_name || 'Unknown'}</span>
                  <span className="text-xs text-gray-400 flex-shrink-0 flex items-center gap-1">
                    <Clock size={11} />{fmtTime(msg.wa_timestamp)}
                  </span>
                </div>
                <div className="flex items-center gap-1 text-xs text-gray-500 mb-1">
                  <Phone size={11} />{msg.from_number}
                </div>
                <p className="text-sm text-gray-700 truncate">
                  {TYPE_ICON[msg.type] || '💬'} {msg.body}
                </p>
              </div>
              {!msg.is_read && (
                <span className="w-2 h-2 rounded-full bg-green-500 flex-shrink-0 mt-2" />
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
